import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
  Play,
  Clock,
  MoreVertical,
  ArrowUpRight,
  Trash2,
  ExternalLink,
  UserPlus,
  Copy,
  Palette,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { generateGradientThumbnailDataUrl } from "@/utils/thumbnailGenerator";
import { studioBOn } from "@/lib/studioB/flag";

interface VideoProject {
  id: string;
  title: string;
  thumbnail?: string;
  slidePreviewUrls?: string[]; // For VIDEO projects: up to 4 slide preview URLs for mosaic display
  duration?: string;
  status: "draft" | "processing" | "completed" | "failed";
  lastModified: Date;
  createdAt: Date;
  fileSize?: number;
  tags: string[];
  description?: string;
  projectType?: 'video' | 'slide';
  hasOutlineTitle?: boolean; // True if title is from generated outline (show full title with smaller font)
  /** Direction B: where the video is (lib/studioB/videoStage), when known. */
  stage?: string | null;
  /** The look it was made in: its name, and the ref that starts another in it. */
  look?: string | null;
  lookRef?: string | null;
}

interface VideoProjectCardProps {
  project: VideoProject;
  onClick: (project: VideoProject) => void;
  onDelete?: (project: VideoProject) => void;
  onShare?: (project: VideoProject) => void;
  onClone?: (project: VideoProject) => void | Promise<void>;
  onAnotherInLook?: (lookRef: string) => void;
  className?: string;
  variant?: "default" | "compact";
}

export function VideoProjectCard({
  project,
  onClick,
  onDelete,
  onShare,
  onClone,
  onAnotherInLook,
  className,
  variant = "default",
}: VideoProjectCardProps) {
  // Track thumbnail load errors to fallback to gradient
  const [thumbnailError, setThumbnailError] = useState(false);
  // Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCloning, setIsCloning] = useState(false);

  // Compute the effective thumbnail source
  const thumbnailSrc = thumbnailError
    ? generateGradientThumbnailDataUrl(project.title)
    : (project.thumbnail || generateGradientThumbnailDataUrl(project.title));

  const compactThumbnailSrc = thumbnailError
    ? generateGradientThumbnailDataUrl(project.title, 64, 64)
    : (project.thumbnail || generateGradientThumbnailDataUrl(project.title, 64, 64));

  if (variant === "compact") {
    return (
      <Card
        className={cn(
          "group relative overflow-hidden cursor-pointer transition-all duration-300",
          "hover:border-primary/40",
          "bg-card border-border",
          className
        )}
        onClick={() => onClick(project)}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Thumbnail or Icon */}
            <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
              <img
                src={compactThumbnailSrc}
                alt={project.title}
                className="w-full h-full object-cover"
                onError={() => setThumbnailError(true)}
                loading="lazy"
              />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className={cn(
                  "font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors",
                  project.hasOutlineTitle ? "text-xs leading-snug" : "text-sm"
                )}>
                  {project.title}
                </h3>
                <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {formatDistanceToNow(project.lastModified, { addSuffix: true })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const b = studioBOn();

  // Handle card click - prevent navigation if delete dialog is open
  const handleCardClick = () => {
    if (showDeleteDialog || isDeleting) return;
    onClick(project);
  };

  return (
    <>
    <Card
      className={cn(
        "group relative overflow-hidden cursor-pointer transition-all duration-300",
        b ? "rounded-[var(--b-radius-card)] border-b-line bg-b-panel shadow-none hover:border-b-line-strong" : "hover:border-primary/40 bg-card border-border",
        className
      )}
      onClick={handleCardClick}
    >
      {/* Thumbnail Section */}
      <div className="relative aspect-video bg-muted overflow-hidden">
        {/* Single thumbnail for all project types (slide 1 preview or gradient fallback) */}
        <img
          src={thumbnailSrc}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={() => setThumbnailError(true)}
          loading="lazy"
        />
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Play Button Overlay */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-14 h-14 rounded-full bg-white dark:bg-black/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100 shadow-xl">
            <Play className="w-6 h-6 text-primary ml-0.5" fill="currentColor" />
          </div>
        </div>
      </div>

      {/* Content Section */}
      <CardContent className="p-4">
        <div className="space-y-2">
          {/* Title and Actions */}
          <div className="flex items-start justify-between gap-2">
            <h3 className={b ? "line-clamp-2 text-b-label text-b-t1" : cn(
              "font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors",
              project.hasOutlineTitle ? "text-sm leading-snug" : "text-base"
            )}>
              {project.title}
            </h3>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick(project);
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open
                </DropdownMenuItem>
                {onAnotherInLook && project.lookRef && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onAnotherInLook(project.lookRef!);
                    }}
                  >
                    <Palette className="w-4 h-4 mr-2" />
                    Another in this look
                  </DropdownMenuItem>
                )}
                {onShare && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onShare(project);
                    }}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Share
                  </DropdownMenuItem>
                )}
                {onClone && (
                  <DropdownMenuItem
                    disabled={isCloning}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (isCloning) return;
                      setIsCloning(true);
                      try {
                        await onClone(project);
                      } finally {
                        setIsCloning(false);
                      }
                    }}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    {isCloning ? "Cloning…" : "Clone"}
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDeleteDialog(true);
                      }}
                      className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Footer - just last modified */}
          {b ? (
            <p data-video-state className="text-b-meta text-b-t3">
              {project.stage && <><span className={project.stage.startsWith("Stopped") ? "text-destructive" : undefined}>{project.stage}</span>{" · "}</>}
              {formatDistanceToNow(project.lastModified, { addSuffix: true })}
            </p>
          ) : (
            <div className="flex items-center text-xs text-muted-foreground">
              <Clock className="w-3 h-3 mr-1" />
              <span>{formatDistanceToNow(project.lastModified, { addSuffix: true })}</span>
            </div>
          )}
          {project.look && <p data-video-look className={b ? "text-b-meta text-b-t3 truncate" : "text-xs text-muted-foreground truncate"}>{project.look}</p>}
        </div>
      </CardContent>
    </Card>

    {/* Delete Confirmation Dialog - rendered outside Card to prevent click bubbling */}
    <ConfirmationDialog
      open={showDeleteDialog}
      onOpenChange={setShowDeleteDialog}
      title="Delete Project"
      description={`Are you sure you want to delete "${project.title}"? This action cannot be undone.`}
      confirmText={isDeleting ? "Deleting..." : "Delete"}
      cancelText="Cancel"
      variant="destructive"
      onConfirm={async () => {
        if (onDelete) {
          setIsDeleting(true);
          try {
            await onDelete(project);
          } finally {
            setIsDeleting(false);
          }
        }
      }}
    />
    </>
  );
}